/**
 * Anleitung zur Erstinstallation der Roboterfirmware (W04).
 *
 * Die Firmware wird getrennt vom normalen Programmupload installiert: sie
 * kommt als UF2-Datei ueber den BOOTSEL-Modus auf das Board, nicht ueber die
 * serielle Verbindung. Der micro:bit-Weg passt hier nicht, und der Hinweis auf
 * microbit.org waere fuer diesen Roboter schlicht falsch.
 */
import { Button, LinkButton, Text } from "@microbit/ui";
import { useCallback } from "react";
import { RiDownloadLine } from "react-icons/ri";
import { FormattedMessage } from "react-intl";
import { HStack, VStack } from "styled-system/jsx";
import DialogHeading from "../../common/DialogHeading";
import { GenericDialog } from "../../common/GenericDialog";
import { FinalFocusRef } from "../../project/project-actions";
import { ConnectErrorChoice } from "./FirmwareDialog";
import { baseUrl } from "../../base";

/**
 * Wo der Build die Firmware ablegt (tools/build-firmware.sh).
 *
 * Relativ zur Basisadresse der Seite: liegt der Editor auf einer Website in
 * einem Unterordner, zeigte ein Pfad ab "/" ins Leere.
 */
export const FIRMWARE_URL = `${baseUrl}firmware/robot-RPI_PICO2_W.uf2`;

/**
 * Warum der Dialog aufgeht.
 *
 * - ``fehlt``: das Board hat MicroPython, aber keine passende Roboterfirmware
 * - ``nicht-gefunden``: aus "Kein Roboter gefunden"; ein neuer Pico meldet
 *   sich ohne Firmware gar nicht
 * - ``menue``: aus dem Hilfe-Menue, ohne Verbindungsversuch
 */
export type FirmwareAnlass = "fehlt" | "nicht-gefunden" | "menue";

interface RobotFirmwareDialogProps {
  callback: (choice: ConnectErrorChoice) => void;
  finalFocusRef: FinalFocusRef;
  anlass?: FirmwareAnlass;
}

const RobotFirmwareDialog = ({
  callback,
  finalFocusRef,
  anlass = "fehlt",
}: RobotFirmwareDialogProps) => {
  const onTryAgain = useCallback(() => {
    callback(ConnectErrorChoice.TRY_AGAIN);
  }, [callback]);
  return (
    <GenericDialog
      finalFocusRef={finalFocusRef}
      body={<RobotFirmwareDialogBody anlass={anlass} />}
      footer={
        <RobotFirmwareDialogFooter
          onClose={() => callback(ConnectErrorChoice.CANCEL)}
          onTryAgain={anlass === "menue" ? undefined : onTryAgain}
        />
      }
      size="3xl"
      onClose={() => callback(ConnectErrorChoice.CANCEL)}
    />
  );
};

const steps = [
  "robot-firmware-step1",
  "robot-firmware-step2",
  "robot-firmware-step3",
  "robot-firmware-step4",
  "robot-firmware-step5",
] as const;

const RobotFirmwareDialogBody = ({ anlass }: { anlass: FirmwareAnlass }) => (
  <VStack
    width="auto"
    ml="auto"
    mr="auto"
    p="5"
    pb="0"
    gap="4"
    alignItems="flex-start"
  >
    <DialogHeading>
      <FormattedMessage id="robot-firmware-title" />
    </DialogHeading>
    <Text>
      <FormattedMessage
        id={
          anlass === "fehlt"
            ? "robot-firmware-intro"
            : "robot-firmware-intro-general"
        }
      />
    </Text>
    <VStack as="ol" gap="2" alignItems="flex-start" pl="5">
      {steps.map((id, index) => (
        <HStack as="li" key={id} gap="3" alignItems="baseline">
          <Text fontWeight="semibold" aria-hidden="true">
            {index + 1}.
          </Text>
          <Text>
            <FormattedMessage id={id} />
          </Text>
        </HStack>
      ))}
    </VStack>
    <Text color="fg.muted">
      <FormattedMessage id="robot-firmware-once" />
    </Text>
  </VStack>
);

interface RobotFirmwareDialogFooterProps {
  onClose: () => void;
  /** Fehlt, wenn kein Verbindungsversuch vorausging. */
  onTryAgain?: () => void;
}

const RobotFirmwareDialogFooter = ({
  onClose,
  onTryAgain,
}: RobotFirmwareDialogFooterProps) => (
  <HStack gap="2.5" justifyContent="flex-end" p="5" width="100%">
    <Button onPress={onClose} size="lg" variant="secondary">
      <FormattedMessage id={onTryAgain ? "cancel-action" : "close-action"} />
    </Button>
    <LinkButton
      href={FIRMWARE_URL}
      download="robot-RPI_PICO2_W.uf2"
      size="lg"
      variant="secondary"
      startIcon={<RiDownloadLine />}
    >
      <FormattedMessage id="robot-firmware-download" />
    </LinkButton>
    {onTryAgain && (
      <Button onPress={onTryAgain} size="lg" variant="primary">
        <FormattedMessage id="robot-firmware-retry" />
      </Button>
    )}
  </HStack>
);

export default RobotFirmwareDialog;
